import axiosInstance from "./apiConfig";
import Cookies from 'js-cookie';
import { clearSession } from './session';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

export const checkStatus = async () => {
  return await axiosInstance.get(`/healthcheck`)
}

export const chat = async (payload) => {
  const response = await fetch(`${apiBaseUrl}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response;
};

export const getChatHistory = async (skip = 0, limit = 50) => {
  const response = await axiosInstance.get(`/chat/history`, {
    params: { skip, limit }
  });
  return response.data;
};

export const getChatById = async (chatId) => {
  const response = await axiosInstance.get(`/chat/${chatId}`);
  return response.data;
};

export const deleteChat = async (chatId) => {
  const response = await axiosInstance.delete(`/chat/${chatId}`);
  return response.data;
};

// Auth
export const login = async (payload) => {
  const response = await axiosInstance.post('/auth/login', payload);
  return response.data;
};

export const forgotPassword = async (payload) => {
  const response = await axiosInstance.post('/auth/forgot-password', payload);
  return response.data;
};

export const register = async (inviteToken, payload) => {
  const response = await axiosInstance.post(`/auth/register/${inviteToken}`, payload);
  return response.data;
};

export const resetPassword = async (resetPasswordToken, payload) => {
  const response = await axiosInstance.post(`/auth/reset-password/${resetPasswordToken}`, payload);
  return response.data;
};

// Admin Users
export const getAdminUsers = async (params = {}) => {
  const response = await axiosInstance.get('/admin/users/', { params });
  return response.data;
};

export const inviteUser = async (payload) => {
  const response = await axiosInstance.post('/admin/users/invite', payload);
  return response.data;
};

export const getUserById = async (userId) => {
  const response = await axiosInstance.get(`/admin/users/${userId}`);
  return response.data;
};

export const updateUser = async (userId, payload) => {
  const response = await axiosInstance.post(`/admin/users/${userId}`, payload);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await axiosInstance.delete(`/admin/users/${userId}`);
  return response.data;
};

export const resendInvite = async (userId) => {
  const response = await axiosInstance.post(`/admin/users/${userId}/resend-invite`);
  return response.data;
};

// Admin Sectors
export const getAdminSectors = async (params = {}) => {
  const response = await axiosInstance.get('/admin/sectors/', { params });
  return response.data;
};

export const createSector = async (payload) => {
  const response = await axiosInstance.post('/admin/sectors/', payload);
  return response.data;
};

export const getSectorById = async (sectorId) => {
  const response = await axiosInstance.get(`/admin/sectors/${sectorId}`);
  return response.data;
};

export const updateSector = async (sectorId, payload) => {
  const response = await axiosInstance.post(`/admin/sectors/${sectorId}`, payload);
  return response.data;
};

export const deleteSector = async (sectorId) => {
  const response = await axiosInstance.delete(`/admin/sectors/${sectorId}`);
  return response.data;
};

// Admin Radar Keywords
export const getAdminRadarKeywords = async (params = {}) => {
  const response = await axiosInstance.get('/admin/radar-keywords/', { params });
  return response.data;
};

export const createRadarKeyword = async (payload) => {
  const response = await axiosInstance.post('/admin/radar-keywords/', payload);
  return response.data;
};

export const getRadarKeywordById = async (radarKeywordId) => {
  const response = await axiosInstance.get(`/admin/radar-keywords/${radarKeywordId}`);
  return response.data;
};

export const updateRadarKeyword = async (radarKeywordId, payload) => {
  const response = await axiosInstance.post(`/admin/radar-keywords/${radarKeywordId}`, payload);
  return response.data;
};

export const deleteRadarKeyword = async (radarKeywordId) => {
  const response = await axiosInstance.delete(`/admin/radar-keywords/${radarKeywordId}`);
  return response.data;
};

// Admin Editorial Areas
export const getAdminEditorialAreas = async (params = {}) => {
  const response = await axiosInstance.get('/admin/editorial-areas/', { params });
  return response.data;
};

export const createEditorialArea = async (payload) => {
  const response = await axiosInstance.post('/admin/editorial-areas/', payload);
  return response.data;
};

export const getEditorialAreaById = async (editorialAreaId) => {
  const response = await axiosInstance.get(`/admin/editorial-areas/${editorialAreaId}`);
  return response.data;
};

export const updateEditorialArea = async (editorialAreaId, payload) => {
  const response = await axiosInstance.post(`/admin/editorial-areas/${editorialAreaId}`, payload);
  return response.data;
};

export const deleteEditorialArea = async (editorialAreaId) => {
  const response = await axiosInstance.delete(`/admin/editorial-areas/${editorialAreaId}`);
  return response.data;
};

// Editorial Intelligence
export const getEditorialIntelligenceFilters = async () => {
  const response = await axiosInstance.get('/admin/editorial-intelligence/filters');
  return response.data;
};

// Filtros persistidos do usuário para a tela de Inteligência Editorial —
// carregados ao acessar a tela para restaurar a última configuração salva.
export const getEditorialIntelligenceUserFilters = async () => {
  const response = await axiosInstance.get('/admin/editorial-intelligence/editorial-intelligence-user-filters');
  return response.data;
};

export const getEditorialIntelligences = async (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v));
    } else if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const response = await axiosInstance.get(`/admin/editorial-intelligence/?${searchParams.toString()}`);
  return response.data;
};

export const getEditorialIntelligenceById = async (id) => {
  const response = await axiosInstance.get(`/admin/editorial-intelligence/${id}`);
  return response.data;
};

// Retorna { editorial_intelligences, total, page, page_size } — paginado no
// servidor via `page` (0-indexed) e `page_size`.
export const getRelatedEditorialIntelligences = async (editorialIntelligenceId, params = {}) => {
  const response = await axiosInstance.get(
    `/admin/editorial-intelligence/${editorialIntelligenceId}/related?${buildSearchString(params)}`
  );
  return response.data;
};

// Editorial Intelligence — Dashboard
const buildSearchString = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => sp.append(key, v));
    } else if (value !== undefined && value !== null && value !== '') {
      sp.append(key, String(value));
    }
  });
  return sp.toString();
};

export const getDashboardFilters = async () => {
  const response = await axiosInstance.get('/admin/editorial-intelligence/dashboard/filters');
  return response.data;
};

// Filtros persistidos do usuário para a tela de Dashboard — carregados ao
// acessar a tela para restaurar a última configuração salva.
export const getDashboardUserFilters = async () => {
  const response = await axiosInstance.get('/admin/editorial-intelligence/dashboard-user-filters');
  return response.data;
};

export const getDashboardOverview = async (params = {}) => {
  const response = await axiosInstance.get(
    `/admin/editorial-intelligence/dashboard/overview?${buildSearchString(params)}`
  );
  return response.data;
};

export const getDashboardRadar = async (params = {}) => {
  const response = await axiosInstance.get(
    `/admin/editorial-intelligence/dashboard/radar?${buildSearchString(params)}`
  );
  return response.data;
};

export const getEditorialIntelligenceArticles = async (editorialIntelligenceId) => {
  const response = await axiosInstance.get(
    `/admin/editorial-intelligence/${editorialIntelligenceId}/articles`
  );
  return response.data;
};

// ─── SSE helpers (compartilhados entre write-article de EI e de Matérias) ───

// Corpo de erro não-SSE (ex.: 400 por limite de fontes NeoFeed excedido) vem
// como JSON `{ detail }`, no mesmo formato usado pelo restante da API. Se não
// for JSON reconhecível, cai no texto bruto (tratado por quem chama).
const parseSseErrorMessage = (text) => {
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    const detail = parsed?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || String(e)).join(', ');
    return null;
  } catch {
    return text;
  }
};

const buildMultipartFormData = ({ instructions, previous_response_id, files, extra = {} }) => {
  const formData = new FormData();
  if (instructions) formData.append('instructions', instructions);
  if (previous_response_id) formData.append('previous_response_id', previous_response_id);
  if (files && files.length > 0) {
    files.forEach((file) => formData.append('files', file));
  }
  Object.entries(extra).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => formData.append(key, v));
    } else if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  return formData;
};

// O endpoint só aceita multipart/form-data (ver api.json), mesmo sem
// arquivos anexados. Enviar como JSON faz o backend perder o
// previous_response_id e quebra a continuidade do histórico.
const streamSseRequest = async (url, formData, { onEvent, onError, signal } = {}) => {
  const token = localStorage.getItem('access_token');
  const headers = {
    Accept: 'text/event-stream',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    signal,
  });

  if (response.status === 401) {
    clearSession();
    const path = window.location.pathname;
    if (path.startsWith('/admin') && path !== '/admin/login') {
      window.location.href = '/admin/login';
    }
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(parseSseErrorMessage(text) || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // SSE blocks are separated by double newlines
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() ?? '';

    for (const block of blocks) {
      if (!block.trim()) continue;
      let eventType = 'message';
      let dataStr = '';

      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataStr += line.slice(5).trim();
        }
      }

      if (!dataStr) continue;

      let data;
      try {
        data = JSON.parse(dataStr);
      } catch {
        data = dataStr;
      }

      if (eventType === 'error') {
        onError?.(data?.detail ?? String(data));
        return;
      }

      onEvent?.({ event: eventType, data });
    }
  }
};

// Admin Articles
export const getAdminArticlesFilters = async () => {
  const response = await axiosInstance.get('/admin/articles/filters');
  return response.data;
};

export const getAdminArticles = async (params = {}) => {
  const response = await axiosInstance.get(`/admin/articles/?${buildSearchString(params)}`);
  return response.data;
};

export const getAdminArticleById = async (articleId) => {
  const response = await axiosInstance.get(`/admin/articles/${articleId}`);
  return response.data;
};

export const deleteAdminArticle = async (articleId) => {
  const response = await axiosInstance.delete(`/admin/articles/${articleId}`);
  return response.data;
};

// Admin Articles — NeoFeed context (sugestões do acervo para usar como
// contexto de estilo/factual na escrita). Body deve trazer pelo menos
// `article_id` ou `editorial_intelligence_id` — validado no back-end.
// Demais filtros (`author`, `tags`, `published_at_start`, `published_at_end`)
// são opcionais e só são enviados quando a página tiver esse dado disponível.
export const getNeoFeedContextSuggestions = async (payload = {}) => {
  const response = await axiosInstance.post('/admin/articles/neofeed-context/suggestions', payload);
  return response.data;
};

// Admin Articles — Geração/reescrita de matéria (SSE). Endpoint único: sem
// `article_id`/`editorial_intelligence_id` cria uma matéria independente nova;
// com `article_id` grava uma nova versão da matéria existente; com
// `editorial_intelligence_id` cria (ou continua) a matéria vinculada àquela
// inteligência editorial.
export const writeAdminArticleStream = async (payload = {}, options = {}) => {
  const {
    files,
    related_editorial_intelligence_ids,
    editorial_intelligence_id,
    article_id,
    editorial_area_id,
    neofeed_context_sources,
    ...rest
  } = payload;
  const formData = buildMultipartFormData({
    instructions: rest.instructions,
    previous_response_id: rest.previous_response_id,
    files,
    extra: {
      related_editorial_intelligence_ids,
      editorial_intelligence_id,
      article_id,
      editorial_area_id,
      neofeed_context_sources,
    },
  });
  await streamSseRequest(`${apiBaseUrl}/admin/articles/write`, formData, options);
};

export default {}
